import type { DayOfWeek } from "@pency/api/tenant/enum";
import { AvailableHours } from "@pency/api/tenant/types";

export const DAYS_IN_ORDER: DayOfWeek[] = [
  "sunday" as DayOfWeek,
  "monday" as DayOfWeek,
  "tuesday" as DayOfWeek,
  "wednesday" as DayOfWeek,
  "thursday" as DayOfWeek,
  "friday" as DayOfWeek,
  "saturday" as DayOfWeek,
];

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

export const isStoreOpen = (
  availableHours?: AvailableHours,
  now: Date = new Date()
): boolean => {
  if (!availableHours || !availableHours.schedule?.length) return true;

  const { dayIndex, minutes } = getTenantNow(availableHours.timezone, now);
  const currentDay = DAYS_IN_ORDER[dayIndex];
  const daySchedule = availableHours.schedule.find((d) => d.day === currentDay);

  if (!daySchedule || !daySchedule.enabled || !daySchedule.ranges?.length) {
    return false;
  }

  return daySchedule.ranges.some((range) => {
    const open = toMinutes(range.open);
    const close = toMinutes(range.close);
    return minutes >= open && minutes < close;
  });
};

const getTenantNow = (timezone: string, now: Date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);

  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "Sun";
  const hour = parts.find((p) => p.type === "hour")?.value ?? "0";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "0";

  const hours = hour === "24" ? 0 : Number(hour);

  return {
    dayIndex: WEEKDAY_INDEX[weekday] ?? 0,
    minutes: hours * 60 + Number(minute),
  };
};

export interface NextOpening {
  day: DayOfWeek;
  time: string;
  offset: number;
}


const pad = (n: number) => String(n).padStart(2, "0");

const formatTimeInViewerTz = (date: Date): string =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

const getViewerDayOffset = (target: Date, now: Date): number => {
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(target) - startOfDay(now)) / 86_400_000);
};

export const getNextOpening = (
  availableHours?: AvailableHours,
  now: Date = new Date()
): NextOpening | null => {
  if (!availableHours || !availableHours.schedule?.length) return null;

  const { dayIndex, minutes } = getTenantNow(availableHours.timezone, now);

  for (let offset = 0; offset < 7; offset++) {
    const targetDay = DAYS_IN_ORDER[(dayIndex + offset) % 7];
    const daySchedule = availableHours.schedule.find(
      (d) => d.day === targetDay
    );

    if (!daySchedule?.enabled || !daySchedule.ranges?.length) continue;

    const sortedRanges = [...daySchedule.ranges].sort(
      (a, b) => toMinutes(a.open) - toMinutes(b.open)
    );

    for (const range of sortedRanges) {
      if (offset > 0 || toMinutes(range.open) > minutes) {
        const deltaMinutes =
          offset * 1440 + (toMinutes(range.open) - minutes);
        const targetDate = new Date(now.getTime() + deltaMinutes * 60_000);

        const viewerOffset = getViewerDayOffset(targetDate, now);
        const viewerDay = DAYS_IN_ORDER[targetDate.getDay()];

        return {
          day: viewerDay,
          time: formatTimeInViewerTz(targetDate),
          offset: viewerOffset,
        };
      }
    }
  }

  return null;
};
